import { Box, Button, Drawer, Stack, Typography } from "@mui/material";
import type { PageFilterGroup, PageFilterSelection } from "./PageLayout.types";
import { FilterGroups } from "./FilterChips";

/** One sheet per page, so a fixed id is unique. */
const TITLE_ID = "page-filter-sheet-title";

interface FilterSheetProps {
	open: boolean;
	onClose: () => void;
	groups: readonly PageFilterGroup[];
	draft: PageFilterSelection;
	onToggle: (groupId: string, value: string) => void;
	onClear: () => void;
	onApply: () => void;
}

/**
 * The phone's filter panel: a modal bottom sheet over the page. Choices apply together on Apply,
 * so the list does not jump while the user is still picking; closing without Apply discards them.
 *
 * A modal `Drawer`, not `widgets/Sheet`: that one is the maps' persistent, draggable, tabbed panel
 * and has no modal or Apply step. The grab bar matches its handle so the two read as one family.
 */
export function FilterSheet(props: FilterSheetProps) {
	return (
		<Drawer
			anchor="bottom"
			open={props.open}
			onClose={props.onClose}
			slotProps={{
				paper: {
					role: "dialog",
					"aria-modal": true,
					"aria-labelledby": TITLE_ID,
					sx: { borderTopLeftRadius: 3, borderTopRightRadius: 3, maxHeight: "80dvh" },
				},
			}}
		>
			<Box aria-hidden sx={{ width: 36, height: 4, borderRadius: 1, bgcolor: "divider", mx: "auto", mt: 1 }} />
			<Typography id={TITLE_ID} variant="subtitle1" component="h2" sx={{ px: 2, pt: 1.5, pb: 1 }}>
				Filters
			</Typography>
			<Box sx={{ px: 2, pb: 2, overflowY: "auto" }}>
				<FilterGroups groups={props.groups} selection={props.draft} onToggle={props.onToggle} />
			</Box>
			<Stack
				direction="row"
				justifyContent="space-between"
				// Clear of the iPhone home indicator (0 until the viewport meta sets viewport-fit=cover).
				sx={{ px: 2, pt: 1.5, pb: "calc(12px + env(safe-area-inset-bottom))", borderTop: 1, borderColor: "divider" }}
			>
				<Button variant="text" onClick={props.onClear}>
					Clear all
				</Button>
				<Button variant="contained" onClick={props.onApply}>
					Apply
				</Button>
			</Stack>
		</Drawer>
	);
}
